import { api } from "../AxiosInstance";
import {
  Truck,
  TruckListResponse,
  TruckFilters,
  CreateTruckRequest,
  UpdateTruckRequest,
} from "../interfaces/truck.interface";
import rbacUtils from "../utils/rbacUtils";

interface UpdateDriverTruckRequest {
  driverId: number;
  oldTruckNumber: string;
  newTruckNumber: string;
  trailerContainerType?: string; // Optional for new trucks
  trailerCapacity?: string; // Optional for new trucks
}

const TruckService = {
  // Get trucks with filters - uses role-based routing
  getTrucks: async (
    filters: TruckFilters = {},
    userProfile?: any
  ): Promise<TruckListResponse> => {
    try {
      const params = new URLSearchParams();

      if (filters.transporterId)
        params.append("transporterId", filters.transporterId.toString());
      if (filters.status) params.append("status", filters.status);
      if (filters.search) params.append("search", filters.search);

      // // Convert skip/limit to page/size for API compatibility
      // if (filters.skip !== undefined && filters.limit !== undefined) {
      //   const page = Math.floor(filters.skip / filters.limit);
      //   params.append("page", page.toString());
      //   params.append("size", filters.limit.toString());
      // }

      // Use skip/limit directly for API
      if (filters.skip !== undefined) {
        params.append("skip", filters.skip.toString());
      }
      if (filters.limit !== undefined) {
        params.append("limit", filters.limit.toString());
      }

      // Use role-based endpoint
      const endpoint = userProfile
        ? rbacUtils.getRoleBasedEndpoint(userProfile, "/trucks")
        : "/enterprise/trucks"; // fallback for backward compatibility

      const response = await api.get(`${endpoint}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching trucks:", error);
      throw error;
    }
  },

  // Get trucks for paginated search select (optimized for dropdowns)
  getTrucksPaginated: async (
    search: string = "",
    page: number = 0,
    pageSize: number = 20,
    userProfile?: any,
    filters: { transporterId?: number; status?: string } = {}
  ): Promise<{
    trucks: any[];
    hasMore: boolean;
    total: number;
  }> => {
    try {
      const params = new URLSearchParams();

      // Add search parameter
      if (search) params.append("search", search);

      // Add pagination
      const skip = page * pageSize;
      params.append("skip", skip.toString());
      params.append("limit", pageSize.toString());

      // Add optional filters
      if (filters.transporterId)
        params.append("transporterId", filters.transporterId.toString());
      if (filters.status) params.append("status", filters.status);

      // Use role-based endpoint
      const endpoint = userProfile
        ? rbacUtils.getRoleBasedEndpoint(userProfile, "/trucks")
        : "/enterprise/trucks";

      const response = await api.get(`${endpoint}?${params.toString()}`);

      // Log API call for debugging
      console.log(
        `[TruckService] Loading trucks: ${endpoint}?${params.toString()}`
      );

      // Handle different response structures
      let trucks = [];
      let total = 0;

      if (response.data?.content) {
        // Paginated response with content array
        trucks = response.data.content;
        total = response.data.totalElements || response.data.total || 0;
        // Direct paginated format
      } else if (Array.isArray(response.data)) {
        // Simple array response (no pagination info, assume more data exists)
        trucks = response.data;
        total = trucks.length + (page + 1) * pageSize; // Assume more data exists
      } else if (response.data?.data) {
        // Nested data structure - check multiple possible locations for pagination info
        if (Array.isArray(response.data.data)) {
          trucks = response.data.data;
        } else if (response.data.data.content) {
          trucks = response.data.data.content;
          total =
            response.data.data.totalElements || response.data.data.total || 0;
        } else {
          trucks = [];
        }

        // If no total found in nested structure, check root level
        if (total === 0) {
          total =
            response.data.totalElements ||
            response.data.total ||
            response.data.data.totalElements ||
            response.data.data.total ||
            0;
        }

        // If still no total and we have trucks, estimate based on current results
        if (total === 0) {
          if (trucks.length === 0 && page > 0) {
            // Empty page after previous pages - we've reached the end
            total = page * pageSize;
          } else if (trucks.length < pageSize) {
            // Partial page - this is the last page
            total = page * pageSize + trucks.length;
          } else if (trucks.length === pageSize) {
            // Full page - we don't know the exact total yet, so don't estimate
            // Let the hasMore logic handle pagination
            total = 0;
          }
        }

        // Log result for debugging
        console.log(
          `[TruckService] Page ${page}: ${trucks.length} trucks, total: ${total}`
        );
      } // Better hasMore calculation
      let hasMore = false;

      if (trucks.length === 0 && page > 0) {
        // Empty page after previous pages - no more data
        hasMore = false;
      } else if (trucks.length < pageSize) {
        // Partial page - this is the last page
        hasMore = false;
      } else if (trucks.length === pageSize) {
        // Full page - there might be more data
        hasMore = true;
      }

      // If we have a reliable total count, use it to override the estimate
      if (total > 0 && skip + trucks.length >= total) {
        hasMore = false;
      }

      console.log(
        `[TruckService] Result: ${trucks.length} trucks, hasMore: ${hasMore}, total: ${total}`
      );

      return {
        trucks,
        hasMore,
        total,
      };
    } catch (error: any) {
      console.error("Error fetching paginated trucks:", error);
      console.error("Error details:", {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      return {
        trucks: [],
        hasMore: false,
        total: 0,
      };
    }
  },

  // Get trucks for transporter user
  getTransporterTrucks: async (
    filters: TruckFilters = {}
  ): Promise<TruckListResponse> => {
    try {
      const params = new URLSearchParams();

      if (filters.status) params.append("status", filters.status);
      if (filters.search) params.append("search", filters.search);

      // // Convert skip/limit to page/size for API compatibility
      // if (filters.skip !== undefined && filters.limit !== undefined) {
      //   const page = Math.floor(filters.skip / filters.limit);
      //   params.append("page", page.toString());
      //   params.append("size", filters.limit.toString());
      // }
      // Use skip/limit directly for API
      if (filters.skip !== undefined) {
        params.append("skip", filters.skip.toString());
      }
      if (filters.limit !== undefined) {
        params.append("limit", filters.limit.toString());
      }

      const response = await api.get(
        `/transporter/trucks?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching transporter trucks:", error);
      throw error;
    }
  },

  // Create new truck
  createTruck: async (truckData: CreateTruckRequest): Promise<Truck> => {
    try {
      const response = await api.post("/fed/truck", truckData);
      return response.data;
    } catch (error) {
      console.error("Error creating truck:", error);
      throw error;
    }
  },

  // Update existing truck
  updateTruck: async (truckData: UpdateTruckRequest): Promise<Truck> => {
    try {
      const response = await api.put("/fed/truck", truckData);
      return response.data;
    } catch (error) {
      console.error("Error updating truck:", error);
      throw error;
    }
  },

  // Delete truck
  deleteTruck: async (truckId: number): Promise<void> => {
    try {
      await api.delete(`/fed/truck/${truckId}`);
    } catch (error) {
      console.error("Error deleting truck:", error);
      throw error;
    }
  },

  // Get single truck by ID
  getTruckById: async (truckId: number): Promise<Truck> => {
    try {
      const response = await api.get(`/fed/truck/${truckId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching truck:", error);
      throw error;
    }
  },

  // Legacy method for updating driver truck (keeping for backward compatibility)
  updateDriverTruck: async (request: UpdateDriverTruckRequest) => {
    try {
      const response = await api.put("/driver/update-truck", request);
      return response;
    } catch (error) {
      console.error("Error updating truck:", error);
      throw error;
    }
  },
};

export default TruckService;
